import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, ChevronRight, Package, AlertCircle, X } from 'lucide-react';
import { ShipmentLineDto, TransitPackagingDto, ShipmentLineRow } from '../../../types/shipmentLine';
import { transitPackagingService } from '../../../services/transitPackagingService';
import { settingsService } from '../../../services/settingsService';
import { NumericInput } from '../../common/inputs';

interface ShipmentLineEditorProps {
  pListId: number;
  orderNo: number;
  initialLines?: ShipmentLineDto[];
  onChange: (lines: ShipmentLineRow[]) => void;
  disabled?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** Når true: 1 kolli per linje, antall låst til 1, "Legg til kolli"-knapp skjult */
  singleParcelMode?: boolean;
  /**
   * TW 36393703: under 1024 px vises kollilinjene som kompakte kort (type, antall, vekt) med
   * tapp → modal for alle felt, og «Legg til kolli» åpner samme modal. Tabellen beholdes på PC.
   * Av (default) = alltid tabell (pakkemodulen).
   */
  compactOnNarrow?: boolean;
}

/** Bredden der ni inline-kolonner slutter å være lesbare (Tailwind lg). */
const COMPACT_MAX_WIDTH = 1023;

function useIsNarrow(enabled: boolean): boolean {
  const query = `(max-width: ${COMPACT_MAX_WIDTH}px)`;
  const [narrow, setNarrow] = useState<boolean>(() =>
    enabled && typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !window.matchMedia) { setNarrow(false); return; }
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setNarrow(e.matches);
    setNarrow(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [enabled, query]);
  return narrow;
}

/**
 * Gjenbrukbar komponent for å registrere kolli-dimensjoner (ShipmentLine).
 * Brukes i DeliveryInfoModal og PackingDetailView.
 */
export const ShipmentLineEditor: React.FC<ShipmentLineEditorProps> = ({
  pListId,
  orderNo,
  initialLines = [],
  onChange,
  disabled = false,
  expanded: controlledExpanded,
  onExpandedChange,
  singleParcelMode = false,
  compactOnNarrow = false
}) => {
  const [lines, setLines] = useState<ShipmentLineRow[]>([]);
  const [transitPackagings, setTransitPackagings] = useState<TransitPackagingDto[]>([]);
  const [isExpanded, setIsExpanded] = useState(controlledExpanded ?? false);
  const [isLoading, setIsLoading] = useState(false);
  const isCompact = useIsNarrow(compactOnNarrow);

  // Kompaktmodus (TW 36393703): én linje redigeres om gangen i en modal.
  // draftIndex = null → ny linje som legges til ved lagring.
  const [draft, setDraft] = useState<{ line: ShipmentLineRow; index: number | null } | null>(null);

  // Høyde-prefill-gating (36334219, bakoverkompatibilitet): med delivery.carrierLineFocus
  // AV (default) prefylles høyde fra bæreren som før — eksisterende tenants beholder dagens
  // oppførsel. Kun i fokusmodus står høyden alltid åpen (pallhøyde varierer per forsendelse).
  const [carrierLineFocus, setCarrierLineFocus] = useState(false);
  useEffect(() => {
    let cancelled = false;
    settingsService.getEffectiveSetting('delivery.carrierLineFocus')
      .then(s => {
        if (!cancelled && s?.currentValue != null) {
          const v = s.currentValue.toString().toLowerCase();
          setCarrierLineFocus(v === 'true' || v === '1' || v === 'ja');
        }
      })
      .catch(() => { /* default false = dagens oppførsel */ });
    return () => { cancelled = true; };
  }, []);

  // Sync controlled expanded state
  useEffect(() => {
    if (controlledExpanded !== undefined) {
      setIsExpanded(controlledExpanded);
    }
  }, [controlledExpanded]);

  // Load transit packagings
  useEffect(() => {
    const loadTransitPackagings = async () => {
      try {
        const data = await transitPackagingService.getAll();
        setTransitPackagings(data);
      } catch (error) {
        console.error('Failed to load transit packagings:', error);
      }
    };
    loadTransitPackagings();
  }, []);

  // Initialize lines from props
  useEffect(() => {
    const mappedLines: ShipmentLineRow[] = initialLines.map(line => ({
      lineID: line.lineID,
      transitPackagingID: line.transitPackagingID,
      transitPackagingDescr: line.transitPackagingDescr,
      description1: line.description1,
      numOfPackages: singleParcelMode ? 1 : line.numOfPackages,
      volume: line.volume,
      weight: line.weight,
      height: line.height,
      width: line.width,
      length: line.length,
      pListID: line.pListID,
      orderNo: line.orderNo,
      sorting: line.sorting,
      isNew: false
    }));
    setLines(mappedLines);
  }, [initialLines, singleParcelMode]);

  // Notify parent of changes
  const notifyChange = useCallback((newLines: ShipmentLineRow[]) => {
    setLines(newLines);
    onChange(newLines);
  }, [onChange]);

  const handleExpandedToggle = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onExpandedChange?.(newExpanded);
  };

  const makeNewLine = (): ShipmentLineRow => ({
    tempId: -Date.now(),
    lineID: '',
    transitPackagingID: null,
    transitPackagingDescr: null,
    description1: '',
    numOfPackages: 1,
    volume: null,
    weight: null,
    height: null,
    width: null,
    length: null,
    pListID: pListId,
    orderNo: orderNo,
    sorting: lines.length,
    isNew: true
  });

  const handleAddLine = () => {
    if (isCompact) {
      setDraft({ line: makeNewLine(), index: null });
      return;
    }
    notifyChange([...lines, makeNewLine()]);
  };

  const handleRemoveLine = (index: number) => {
    const newLines = lines.filter((_, i) => i !== index);
    notifyChange(newLines);
  };

  /**
   * Felles feltendring for tabell og modal: bærer-defaults ved typevalg og
   * auto-beregnet volum fra L×B×H. Ren funksjon på én linje.
   */
  const applyFieldChange = useCallback((source: ShipmentLineRow, field: keyof ShipmentLineRow, value: any): ShipmentLineRow => {
    const line = { ...source, [field]: value, isModified: true } as ShipmentLineRow;

    // In singleParcelMode, force numOfPackages to 1
    if (singleParcelMode && field === 'numOfPackages') {
      line.numOfPackages = 1;
    }

    // If transit packaging changed, apply defaults.
    // HØYDE prefylles kun utenfor fokusmodus (36334224/36334219): i fokusmodus varierer
    // pallhøyden per forsendelse og skal stå åpen — med settingen AV beholdes dagens
    // prefill-oppførsel for eksisterende tenants.
    if (field === 'transitPackagingID' && value) {
      const tp = transitPackagings.find(t => t.transitPackagingID === value);
      if (tp) {
        line.transitPackagingDescr = tp.transitPackagingDescr;
        if (tp.weight) line.weight = tp.weight;
        if (tp.volume) line.volume = tp.volume;
        if (tp.width) line.width = tp.width;
        if (tp.length) line.length = tp.length;
        if (!carrierLineFocus && tp.height) line.height = tp.height;
      }
    }

    // Auto-calculate volume if all dimensions are set
    if (['height', 'width', 'length'].includes(field as string)) {
      const h = field === 'height' ? value : line.height;
      const w = field === 'width' ? value : line.width;
      const l = field === 'length' ? value : line.length;
      if (h && w && l) {
        line.volume = Math.round((h * w * l) / 1_000_000 * 1_000_000) / 1_000_000;
      }
    }

    return line;
  }, [singleParcelMode, transitPackagings, carrierLineFocus]);

  const handleLineChange = (index: number, field: keyof ShipmentLineRow, value: any) => {
    const newLines = [...lines];
    newLines[index] = applyFieldChange(newLines[index], field, value);
    notifyChange(newLines);
  };

  // --- Modal (kompaktmodus) ---
  const handleDraftChange = (field: keyof ShipmentLineRow, value: any) => {
    setDraft(prev => prev ? { ...prev, line: applyFieldChange(prev.line, field, value) } : prev);
  };

  const handleDraftSave = () => {
    if (!draft) return;
    const saved: ShipmentLineRow = {
      ...draft.line,
      numOfPackages: singleParcelMode ? 1 : Math.max(1, draft.line.numOfPackages || 1)
    };
    if (draft.index === null) {
      notifyChange([...lines, saved]);
    } else {
      const newLines = [...lines];
      newLines[draft.index] = saved;
      notifyChange(newLines);
    }
    setDraft(null);
  };

  const handleDraftDelete = () => {
    if (!draft || draft.index === null) { setDraft(null); return; }
    handleRemoveLine(draft.index);
    setDraft(null);
  };

  const totalPackages = lines.reduce((sum, l) => sum + (l.numOfPackages || 0), 0);
  const totalWeight = lines.reduce((sum, l) => sum + ((l.weight || 0) * (l.numOfPackages || 0)), 0);
  const totalVolume = lines.reduce((sum, l) => sum + ((l.volume || 0) * (l.numOfPackages || 0)), 0);

  const fieldInput = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[44px]';

  const renderCompactCards = () => (
    <div className="space-y-2 mb-4">
      {lines.map((line, index) => {
        const dims = [line.length, line.width, line.height].every(v => v != null && v > 0)
          ? `${line.length}×${line.width}×${line.height} cm`
          : null;
        return (
          <button
            key={line.lineID || line.tempId}
            type="button"
            onClick={() => setDraft({ line: { ...line }, index })}
            disabled={disabled}
            className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white hover:bg-purple-50 text-left min-h-[56px] disabled:opacity-60"
          >
            <Package size={20} className="text-purple-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-sm">
                <span className={`font-medium truncate ${line.transitPackagingDescr ? 'text-gray-900' : 'text-amber-700'}`}>
                  {line.transitPackagingDescr || 'Velg type'}
                </span>
                {!singleParcelMode && (
                  <span className="text-gray-600 flex-shrink-0">× {line.numOfPackages || 1}</span>
                )}
              </div>
              <div className="text-xs text-gray-600 truncate">
                {line.weight != null && line.weight > 0 ? `${line.weight} kg` : 'Vekt mangler'}
                {dims && ` · ${dims}`}
                {line.description1 && ` · ${line.description1}`}
              </div>
            </div>
            <ChevronRight size={18} className="text-gray-400 flex-shrink-0" />
          </button>
        );
      })}
    </div>
  );

  const renderDraftModal = () => {
    if (!draft) return null;
    const l = draft.line;
    return (
      <div className="fixed inset-0 z-[60] flex items-end tablet:items-center justify-center bg-black/40 p-0 tablet:p-4" onClick={() => setDraft(null)}>
        <div
          className="bg-white rounded-t-xl tablet:rounded-xl shadow-xl w-full max-w-md max-h-[92vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900">
              {draft.index === null ? 'Legg til kolli' : 'Rediger kolli'}
            </h3>
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="p-2 rounded-lg hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Lukk"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-4 space-y-3 overflow-y-auto">
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Type</span>
              <select
                className={fieldInput}
                value={l.transitPackagingID || ''}
                onChange={(e) => handleDraftChange('transitPackagingID', e.target.value ? parseInt(e.target.value) : null)}
                disabled={disabled}
              >
                <option value="">-- Velg --</option>
                {transitPackagings.map(tp => (
                  <option key={tp.transitPackagingID} value={tp.transitPackagingID}>
                    {tp.transitPackagingDescr}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-2 gap-3">
              {!singleParcelMode && (
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Antall</span>
                  <NumericInput
                    mode="integer"
                    className={fieldInput}
                    value={l.numOfPackages === 0 ? '' : String(l.numOfPackages)}
                    onChange={(e) => handleDraftChange('numOfPackages', e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
                    onBlur={(e) => { if (e.target.value === '' || parseInt(e.target.value, 10) < 1) handleDraftChange('numOfPackages', 1); }}
                    disabled={disabled}
                  />
                </label>
              )}
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Vekt (kg)</span>
                <NumericInput
                  mode="decimal"
                  className={fieldInput}
                  value={l.weight ?? ''}
                  onChange={(e) => handleDraftChange('weight', e.target.value === '' ? null : parseFloat(e.target.value))}
                  disabled={disabled}
                />
              </label>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">L (cm)</span>
                <NumericInput mode="decimal" className={fieldInput} value={l.length ?? ''} disabled={disabled}
                  onChange={(e) => handleDraftChange('length', e.target.value === '' ? null : parseFloat(e.target.value))} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">B (cm)</span>
                <NumericInput mode="decimal" className={fieldInput} value={l.width ?? ''} disabled={disabled}
                  onChange={(e) => handleDraftChange('width', e.target.value === '' ? null : parseFloat(e.target.value))} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">H (cm)</span>
                <NumericInput mode="decimal" className={fieldInput} value={l.height ?? ''} disabled={disabled}
                  onChange={(e) => handleDraftChange('height', e.target.value === '' ? null : parseFloat(e.target.value))} />
              </label>
            </div>

            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Volum (m³) — beregnes fra L×B×H</span>
              <NumericInput
                mode="decimal"
                className={`${fieldInput} bg-gray-50`}
                value={l.volume ?? ''}
                onChange={(e) => handleDraftChange('volume', e.target.value === '' ? null : parseFloat(e.target.value))}
                disabled={disabled}
              />
            </label>

            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Beskrivelse</span>
              <input
                type="text"
                maxLength={200}
                className={fieldInput}
                value={l.description1 || ''}
                onChange={(e) => handleDraftChange('description1', e.target.value)}
                disabled={disabled}
                placeholder="Beskrivelse (valgfritt)"
              />
            </label>
          </div>

          <div className="flex items-center gap-2 px-4 py-3 border-t border-gray-200">
            {draft.index !== null && !singleParcelMode && (
              <button
                type="button"
                onClick={handleDraftDelete}
                disabled={disabled}
                className="flex items-center gap-1.5 px-3 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 min-h-[44px]"
              >
                <Trash2 size={16} />
                Slett
              </button>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg min-h-[44px]"
            >
              Avbryt
            </button>
            <button
              type="button"
              onClick={handleDraftSave}
              disabled={disabled}
              className="px-5 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 rounded-lg min-h-[44px]"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTable = () => (
    <div className="overflow-x-auto mb-4">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            {!singleParcelMode && (
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Antall</th>
            )}
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vekt (kg)</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">L (cm)</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">B (cm)</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">H (cm)</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vol (m³)</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Beskr.</th>
            {!singleParcelMode && (
              <th className="px-2 py-2 w-10"></th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {lines.map((line, index) => (
            <tr key={line.lineID || line.tempId} className="hover:bg-gray-50">
              <td className="px-2 py-2">
                <select
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={line.transitPackagingID || ''}
                  onChange={(e) => handleLineChange(index, 'transitPackagingID', e.target.value ? parseInt(e.target.value) : null)}
                  disabled={disabled}
                >
                  <option value="">-- Velg --</option>
                  {transitPackagings.map(tp => (
                    <option key={tp.transitPackagingID} value={tp.transitPackagingID}>
                      {tp.transitPackagingDescr}
                    </option>
                  ))}
                </select>
              </td>
              {!singleParcelMode && (
                <td className="px-2 py-2">
                  <NumericInput
                    mode="integer"
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    // Tom verdi lagres som 0 (sentinel) slik at operatøren faktisk kan
                    // viske ut default-"1" på nettbrett; defaulter til 1 først på blur.
                    value={line.numOfPackages === 0 ? '' : String(line.numOfPackages)}
                    onChange={(e) => handleLineChange(index, 'numOfPackages', e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
                    onBlur={(e) => { if (e.target.value === '' || parseInt(e.target.value, 10) < 1) handleLineChange(index, 'numOfPackages', 1); }}
                    disabled={disabled}
                  />
                </td>
              )}
              <td className="px-2 py-2">
                <NumericInput
                  mode="decimal"
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={line.weight ?? ''}
                  onChange={(e) => handleLineChange(index, 'weight', e.target.value === '' ? null : parseFloat(e.target.value))}
                  disabled={disabled}
                />
              </td>
              <td className="px-2 py-2">
                <NumericInput
                  mode="decimal"
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={line.length ?? ''}
                  onChange={(e) => handleLineChange(index, 'length', e.target.value === '' ? null : parseFloat(e.target.value))}
                  disabled={disabled}
                />
              </td>
              <td className="px-2 py-2">
                <NumericInput
                  mode="decimal"
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={line.width ?? ''}
                  onChange={(e) => handleLineChange(index, 'width', e.target.value === '' ? null : parseFloat(e.target.value))}
                  disabled={disabled}
                />
              </td>
              <td className="px-2 py-2">
                <NumericInput
                  mode="decimal"
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={line.height ?? ''}
                  onChange={(e) => handleLineChange(index, 'height', e.target.value === '' ? null : parseFloat(e.target.value))}
                  disabled={disabled}
                />
              </td>
              <td className="px-2 py-2">
                <NumericInput
                  mode="decimal"
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm bg-gray-50 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={line.volume ?? ''}
                  onChange={(e) => handleLineChange(index, 'volume', e.target.value === '' ? null : parseFloat(e.target.value))}
                  disabled={disabled}
                  title="Beregnes automatisk fra L×B×H"
                />
              </td>
              <td className="px-2 py-2">
                <input
                  type="text"
                  maxLength={200}
                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={line.description1 || ''}
                  onChange={(e) => handleLineChange(index, 'description1', e.target.value)}
                  disabled={disabled}
                  placeholder="Beskrivelse"
                />
              </td>
              {!singleParcelMode && (
                <td className="px-2 py-2">
                  <button
                    onClick={() => handleRemoveLine(index)}
                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                    disabled={disabled}
                    title="Slett linje"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={handleExpandedToggle}
        className="w-full flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 transition-colors"
        disabled={disabled}
      >
        <div className="flex items-center gap-2">
          <Package size={20} className="text-purple-600" />
          <span className="font-medium text-purple-900">
            Transport-detaljer
          </span>
          {lines.length > 0 && (
            <span className="text-sm text-purple-600 ml-2">
              ({totalPackages} kolli, {totalWeight.toFixed(2)} kg, {totalVolume.toFixed(4)} m³)
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp size={20} className="text-purple-600" />
        ) : (
          <ChevronDown size={20} className="text-purple-600" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 bg-white">
          {isLoading ? (
            <div className="text-center py-4 text-gray-500">Laster...</div>
          ) : (
            <>
              {/* Single parcel mode info banner */}
              {singleParcelMode && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <strong>1 kolli per sending.</strong> Denne transportøren krever separate sendinger per kolli. Registrer vekt for hvert kolli.
                  </div>
                </div>
              )}

              {/* Kollilinjer: kompakte kort på smal skjerm (TW 36393703), ellers tabell */}
              {lines.length > 0 ? (
                isCompact ? renderCompactCards() : renderTable()
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  {singleParcelMode
                    ? 'Angi antall kolli ovenfor for å registrere vekt per kolli.'
                    : 'Ingen kolli-detaljer registrert. Klikk "Legg til kolli" for å registrere.'}
                </div>
              )}

              {/* Add button - hidden in singleParcelMode (lines are managed by DeliveryInfoModal) */}
              {!singleParcelMode && (
                <button
                  onClick={handleAddLine}
                  className="flex items-center gap-2 px-4 py-2 text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors min-h-[44px]"
                  disabled={disabled}
                >
                  <Plus size={16} />
                  Legg til kolli
                </button>
              )}

              {/* Summary */}
              {lines.length > 0 && (
                <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                  <div className="text-sm text-purple-800">
                    <strong>Totalt:</strong> {totalPackages} kolli, {totalWeight.toFixed(2)} kg, {totalVolume.toFixed(4)} m³
                    {singleParcelMode && (
                      <span className="ml-2 text-amber-700">({totalPackages} sendinger)</span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {renderDraftModal()}
    </div>
  );
};

export default ShipmentLineEditor;
